/**
 * A12 — Onboarding Step 4/4: Elegir modulos (non-celulares verticals)
 *
 * Toggle grid with v1 (available) and next (coming soon) modules.
 * On submit: INSERT into rol_modulos_permitidos, then redirect to /empresa/dashboard.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { supabase } from '@shared/lib/supabaseClient';
import OnboardingLayout from '../../components/OnboardingLayout';
import { useOnboarding } from '../../contexts/OnboardingContext';

// ── Module definitions ───────────────────────────────────────────────────────

interface ModuleDef {
  key: string;
  name: string;
  tier: 'v1' | 'next';
  desc: string;
}

const MODULES: ModuleDef[] = [
  { key: 'crm', name: 'CRM', tier: 'v1', desc: 'Clientes, leads, pipeline de ventas.' },
  { key: 'cotizaciones', name: 'Cotizaciones', tier: 'v1', desc: 'Editor de cotizaciones + PDF + link publico.' },
  { key: 'ordenes', name: 'Ordenes', tier: 'v1', desc: 'Ordenes de trabajo con fases y operarios.' },
  { key: 'inventario', name: 'Inventario', tier: 'v1', desc: 'Materiales, almacenes y reservas.' },
  { key: 'facturacion', name: 'Facturacion', tier: 'v1', desc: 'Facturas, cuentas por cobrar, notas de credito.' },
  { key: 'notificaciones', name: 'Notificaciones', tier: 'v1', desc: 'Bandeja in-app + email + WhatsApp.' },
  { key: 'produccion', name: 'Produccion', tier: 'next', desc: 'Vista agregada del taller, capacidad por oficio.' },
  { key: 'logistica', name: 'Logistica', tier: 'next', desc: 'Rutas del chofer, entregas con firma + foto.' },
  { key: 'cobros', name: 'Cobros', tier: 'next', desc: 'Registro de pagos, conciliacion bancaria.' },
  { key: 'rrhh', name: 'RRHH ligero', tier: 'next', desc: 'Empleados, asistencia, dias libres.' },
  { key: 'contabilidad', name: 'Contabilidad', tier: 'next', desc: 'Asientos automaticos + export al contador.' },
  { key: 'proveedores', name: 'Proveedores', tier: 'next', desc: 'Catalogo + ordenes de compra.' },
];

// ── Component ────────────────────────────────────────────────────────────────

const ModulesPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setSelectedModules } = useOnboarding();
  const { selectedModules, empresaId } = state;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeCount = Object.values(selectedModules).filter(Boolean).length;

  const toggle = (key: string) => {
    setSelectedModules({ ...selectedModules, [key]: !selectedModules[key] });
  };

  const handleNext = async () => {
    setError(null);
    setLoading(true);

    try {
      // Save enabled modules
      if (empresaId) {
        const enabledModules = Object.entries(selectedModules)
          .filter(([_, on]) => on)
          .map(([key]) => key);

        const rows = enabledModules.map((modKey) => ({
          empresa_id: empresaId,
          modulo: modKey,
          activo: true,
        }));

        if (rows.length > 0) {
          const { error: dbErr } = await (supabase as any)
            .from('empresa_modulos')
            .upsert(rows, { onConflict: 'empresa_id,modulo' });

          if (dbErr) {
            console.error('[Modules] Upsert error:', dbErr);
            // Non-blocking
          }
        }

        // Mark onboarding as complete
        await (supabase as any)
          .from('empresas')
          .update({ onboarding_completado: true })
          .eq('id', empresaId);
      }

      // Redirect to empresa app dashboard
      window.location.href = '/empresa/dashboard';
    } catch (err: any) {
      setError(err?.message || 'Error inesperado.');
      setLoading(false);
    }
  };

  const handleSkip = () => {
    window.location.href = '/empresa/dashboard';
  };

  return (
    <OnboardingLayout
      step={4}
      onBack={() => navigate('/onboarding/negocio')}
      onNext={handleNext}
      nextLabel="Entrar al workspace"
      loading={loading}
      skipLabel="Activar todo despues"
      onSkip={handleSkip}
    >
      <h1 className="text-3xl font-bold tracking-tight">Activa los modulos que necesitas.</h1>
      <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[56ch] mb-4">
        Puedes encender o apagar cualquier modulo desde Settings cuando quieras -- no se borran datos. Los marcados <em>next</em> aun estan en desarrollo; te avisamos cuando esten listos.
      </p>

      {error && (
        <Alert variant="destructive" className="mb-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Module grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 my-3">
        {MODULES.map((mod) => {
          const isOn = !!selectedModules[mod.key];

          return (
            <div
              key={mod.key}
              onClick={() => toggle(mod.key)}
              className={[
                'flex gap-3 items-start p-3.5 rounded-md cursor-pointer transition-all border',
                isOn
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-muted-foreground/40',
              ].join(' ')}
            >
              {/* Checkbox indicator */}
              <span
                className={[
                  'w-[18px] h-[18px] rounded flex-shrink-0 mt-0.5 flex items-center justify-center border-[1.5px]',
                  isOn
                    ? 'bg-primary border-primary'
                    : 'bg-card border-border',
                ].join(' ')}
              >
                {isOn && <Check className="w-3 h-3 text-primary-foreground" />}
              </span>

              <div>
                {/* Name + tier badge */}
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-sm">{mod.name}</span>
                  <span
                    className={[
                      'font-mono text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border',
                      mod.tier === 'next'
                        ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                        : 'bg-green-50 text-green-700 border-green-200',
                    ].join(' ')}
                  >
                    {mod.tier}
                  </span>
                </div>
                <p className="text-[12px] text-muted-foreground leading-snug">{mod.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[13px] text-muted-foreground italic">
        {activeCount} modulo{activeCount !== 1 ? 's' : ''} activo{activeCount !== 1 ? 's' : ''} - puedes cambiar esto en Settings &rarr; Modulos
      </p>
    </OnboardingLayout>
  );
};

export default ModulesPage;
